import { Component, ElementRef, OnInit, AfterViewInit, DoCheck, KeyValueDiffers, KeyValueDiffer, ViewEncapsulation, ChangeDetectorRef, inject, input, output, contentChild, contentChildren, viewChild, linkedSignal, effect, computed, AfterContentInit, OnDestroy, TemplateRef } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { INgxDatatableConfig } from '../types/ngx-datatable-config.type';
import { groupRowsByParents, optionalGetterForProp } from '../utils/tree';
import { TableColumn } from '../types/table-column.type';
import { setColumnDefaults, translateTemplates } from '../utils/column-helper';
import { ColumnMode } from '../types/column-mode.type';
import { SelectionType } from '../types/selection.type';
import { SortType } from '../types/sort.type';
import { SortPropDir } from '../types/sort-prop-dir.type';
import { ContextmenuType } from '../types/contextmenu.type';
import { ScrollbarHelper } from '../services/scrollbar-helper.service';
import { ColumnChangesService } from '../services/column-changes.service';
import { DimensionsHelper } from '../services/dimensions-helper.service';
import { throttleable } from '../utils/throttle';
import { forceFillColumnWidths, adjustColumnWidths } from '../utils/math';
import { sortRows } from '../utils/sort';
import { VisibilityDirective } from '../directives/visibility.directive';
import { DataTableHeaderComponent } from './header/header.component';
import { DataTableBodyComponent } from './body/body.component';
import { DatatableFooterDirective } from './footer/footer.directive';
import { DatatableRowDetailDirective } from './row-detail/row-detail.directive';
import { DataTableColumnDirective } from './columns/column.directive';
import { DatatableGroupHeaderDirective } from './body/body-group-header.directive';
import { DataTableFooterComponent } from './footer/footer.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'ngx-datatable',
  templateUrl: './datatable.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: [
    'datatable.styles.base.component.scss',
    'datatable.styles.custom.component.scss',
    'datatable.styles.responsive.component.scss'
  ],
  host: {
    class: 'ngx-datatable',
    '(window:resize)': 'onWindowResize()',
    '[class.fixed-header]': 'typeof headerHeight() === "string" ? headerHeight() !== "auto" : true',
    '[class.fixed-row]': 'rowHeight() !== "auto"',
    '[class.scroll-vertical]': 'scrollbarV()',
    '[class.virtualized]': 'virtualization()',
    '[class.scroll-horz]': 'scrollbarH()',
    '[class.selectable]': 'selectionType() !== undefined',
    '[class.checkbox-selection]': 'selectionType() === "checkbox"',
    '[class.cell-selection]': 'selectionType() === "cell"',
    '[class.single-selection]': 'selectionType() === "single"',
    '[class.multi-selection]': 'selectionType() === "multi"',
    '[class.multi-click-selection]': 'selectionType() === "multiClick"'
  },
  imports: [
    VisibilityDirective,
    DataTableHeaderComponent,
    DataTableBodyComponent,
    DataTableFooterComponent,
    AsyncPipe
  ]
})
export class DatatableComponent implements OnInit, DoCheck, AfterViewInit, AfterContentInit, OnDestroy {
  private readonly scrollbarHelper = inject(ScrollbarHelper, { skipSelf: true });
  private readonly dimensionsHelper = inject(DimensionsHelper, { skipSelf: true });
  private readonly cd = inject(ChangeDetectorRef);
  private readonly columnChangesService = inject(ColumnChangesService);
  private readonly configuration = inject<INgxDatatableConfig>('configuration' as never, { optional: true });

  /**
   * Template for the target marker of drag target columns.
   */
  readonly targetMarkerTemplate = input<TemplateRef<unknown>>(undefined);

  /**
   * Aria label for <ngx-datatable>.
   */
  readonly tableAriaLabel = input<string>(undefined);

  /**
   * Rows that are displayed in the table.
   */
  readonly rowsInput = input<Array<unknown>>(undefined, { alias: 'rows' });

  /**
   * This attribute allows the user to set the name of the column to group the data with
   */
  readonly groupRowsBy = input<string>(undefined);

  /**
   * This attribute allows the user to set a grouped array in the following format:
   *  [
   *    {groupid=1} [
   *      {id=1 name="test1"},
   *      {id=2 name="test2"},
   *      {id=3 name="test3"}
   *    ]},
   *    {groupid=2>[
   *      {id=4 name="test4"},
   *      {id=5 name="test5"},
   *      {id=6 name="test6"}
   *    ]}
   *  ]
   */
  readonly groupedRowsInput = input<Array<Record<string, unknown>>>(undefined, { alias: 'groupedRows' });
  readonly groupedRows = computed<Array<Record<string, unknown>> | undefined>(() => {
    const groupBy = this.groupRowsBy();
    const rows = this.rowsInput();
    if (groupBy && rows) {
      return this.groupArrayBy(rows, groupBy);
    }

    return this.groupedRowsInput();
  });

  /**
   * Columns to be displayed.
   */
  readonly columnsInput = input<Array<TableColumn>>(undefined, { alias: 'columns' });

  /**
   * List of row objects that should be
   * represented as selected in the grid.
   * Default value: `[]`
   */
  readonly selectedInput = input<Array<unknown>>([], { alias: 'selected' });
  readonly selected = linkedSignal<Array<unknown>>(() => this.selectedInput());

  /**
   * Enable vertical scrollbars
   */
  readonly scrollbarV = input<boolean>(false);

  /**
   * Enable horz scrollbars
   */
  readonly scrollbarH = input<boolean>(false);

  /**
   * The row height; which is necessary
   * to calculate the height for the lazy rendering.
   */
  readonly rowHeight = input<number | 'auto' | ((row?: unknown) => number)>(30);

  /**
   * Type of column width distribution formula.
   * Example: flex, force, standard
   */
  readonly columnMode = input<ColumnMode | keyof typeof ColumnMode>(ColumnMode.standard);

  /**
   * The minimum header height in pixels.
   * Pass a falsey for no header
   */
  readonly headerHeight = input<number | 'auto'>(30);

  /**
   * The minimum footer height in pixels.
   * Pass falsey for no footer
   */
  readonly footerHeight = input<number | 'auto'>(0);

  /**
   * If the table should use external paging
   * otherwise its assumed that all data is preloaded.
   */
  readonly externalPaging = input<boolean>(false);

  /**
   * If the table should use external sorting or
   * the built-in basic sorting.
   */
  readonly externalSorting = input<boolean>(false);

  /**
   * The page size to be shown.
   * Default value: `undefined`
   */
  readonly limitInput = input<number | undefined>(undefined, { alias: 'limit' });

  /**
   * The total count of all rows.
   * Default value: `0`
   */
  readonly countInput = input<number>(0, { alias: 'count' });

  /**
   * The current offset ( page - 1 ) shown.
   * Default value: `0`
   */
  readonly offsetInput = input<number>(0, { alias: 'offset' });
  /**
   * Internal writable offset, clamped to valid page range via currentOffset.
   * Can be set programmatically (e.g. reset to 0 after filtering).
   */
  readonly _offsetSignal = linkedSignal<number>(() => this.offsetInput());

  /**
   * Show the linear loading bar.
   * Default value: `false`
   */
  readonly loadingIndicator = input<boolean>(false);

  /**
   * Type of row selection. Options are:
   *
   *  - `single`
   *  - `multi`
   *  - `checkbox`
   *  - `multiClick`
   *  - `cell`
   *
   * For no selection pass a `falsey`.
   * Default value: `undefined`
   */
  readonly selectionType = input<SelectionType>(undefined);

  /**
   * Enable/Disable ability to re-order columns
   * by dragging them.
   */
  readonly reorderable = input<boolean>(true);

  /**
   * Swap columns on re-order columns or
   * move them.
   */
  readonly swapColumns = input<boolean>(true);

  /**
   * The type of sorting
   */
  readonly sortType = input<SortType>(SortType.single);

  /**
   * Array of sorted columns by property and type.
   * Default value: `[]`
   */
  readonly sortsInput = input<Array<SortPropDir>>([], { alias: 'sorts' });
  readonly sorts = linkedSignal<Array<SortPropDir>>(() => this.sortsInput());

  /**
   * Css class overrides
   */
  readonly cssClasses = input<{
    sortAscending?: string;
    sortDescending?: string;
    sortUnset?: string;
    pagerLeftArrow?: string;
    pagerRightArrow?: string;
    pagerPrevious?: string;
    pagerNext?: string;
  }>({
    sortAscending: 'datatable-icon-up',
    sortDescending: 'datatable-icon-down',
    sortUnset: 'datatable-icon-sort-unset',
    pagerLeftArrow: 'datatable-icon-left',
    pagerRightArrow: 'datatable-icon-right',
    pagerPrevious: 'datatable-icon-prev',
    pagerNext: 'datatable-icon-skip'
  });

  /**
   * Message overrides for localization
   *
   * emptyMessage     [default] = 'No data to display'
   * totalMessage     [default] = 'total'
   * selectedMessage  [default] = 'selected'
   */
  readonly messagesInput = input<{ emptyMessage?: string; totalMessage?: string; selectedMessage?: string }>({
    // Message to show when array is presented
    // but contains no values
    emptyMessage: 'No data to display',
    // Footer total message
    totalMessage: 'total',
    // Footer selected message
    selectedMessage: 'selected'
  }, { alias: 'messages' });

  readonly messages = linkedSignal<{ emptyMessage?: string; totalMessage?: string; selectedMessage?: string }>(() => this.messagesInput());

  /**
   * Row specific classes.
   * Similar implementation to ngClass.
   *
   *  [rowClass]="'first second'"
   *  [rowClass]="{ 'first': true, 'second': true, 'third': false }"
   */
  readonly rowClass = input<(row: unknown) => string | Record<string, boolean>>(undefined);

  /**
   * A boolean/function you can use to check whether you want
   * to select a particular row based on a criteria. Example:
   *
   *    (selection) => {
   *      return selection !== 'Ethel Price';
   *    }
   */
  readonly selectCheck = input<(row: unknown) => boolean>(undefined);

  /**
   * A function you can use to check whether you want
   * to show the checkbox for a particular row based on a criteria. Example:
   *
   *    (row, column, value) => {
   *      return row.name !== 'Ethel Price';
   *    }
   */
  readonly displayCheck = input<(row: unknown, column?: unknown, value?: unknown) => boolean>(undefined);

  /**
   * A boolean you can use to set the detault behaviour of rows and groups
   * whether they will start expanded or not. If ommited the default is NOT expanded.
   *
   */
  readonly groupExpansionDefault = input<boolean>(false);

  /**
   * Property to which you can use for custom tracking of rows.
   * Example: 'name'
   */
  readonly trackByProp = input<string>(undefined);

  /**
   * Property to which you can use for determining select all
   * rows on current page or not.
   *
   * @memberOf DatatableComponent
   */
  readonly selectAllRowsOnPage = input(false);

  /**
   * A flag for row virtualization on / off
   */
  readonly virtualization = input<boolean>(true);

  /**
   * Tree from relation
   */
  readonly treeFromRelation = input<string>(undefined);

  /**
   * Tree to relation
   */
  readonly treeToRelation = input<string>(undefined);

  /**
   * A flag for switching summary row on / off
   */
  readonly summaryRow = input<boolean>(false);

  /**
   * A height of summary row
   */
  readonly summaryHeight = input<number | 'auto'>(30);

  /**
   * A property holds a summary row position: top/bottom
   */
  readonly summaryPosition = input<string>('top');

  /**
   * Body was scrolled typically in a `scrollbarV:true` scenario.
   */
  readonly scroll = output<{ offsetY: number; offsetX: number }>();

  /**
   * A cell or row was focused via keyboard or mouse click.
   */
  readonly activate = output<unknown>();

  /**
   * A cell or row was selected.
   */
  readonly select = output<{ selected: Array<unknown> }>();

  /**
   * Column sort was invoked.
   */
  readonly sort = output<{ sorts: Array<SortPropDir>; newValue?: string; prevValue?: string }>();

  /**
   * The table was paged either triggered by the pager or the body scroll.
   */
  readonly page = output<{ count: number; pageSize: number; limit: number; offset: number }>();

  /**
   * Columns were re-ordered.
   */
  readonly reorder = output<unknown>();

  /**
   * Column was resized.
   */
  readonly resize = output<unknown>();

  /**
   * The context menu was invoked on the table.
   * type indicates whether the header or the body was clicked.
   * content contains either the column or the row that was clicked.
   */
  readonly tableContextmenu = output<{
    event: MouseEvent;
    type: ContextmenuType;
    content: unknown;
}>();

  /**
   * A row was expanded ot collapsed for tree
   */
  readonly treeAction = output<unknown>();

  /**
   * Column templates gathered from `ContentChildren`
   * if described in your markup.
   *
   * Signal-based `contentChildren()` query — fires reactively whenever
   * the projected `<ngx-datatable-column>` set changes. Necessary because
   * the legacy decorator-based `@ContentChildren` setter is not reliably
   * invoked when mixed with signal-based `contentChild()` queries on
   * other slots (Angular 21+).
   */
  readonly columnTemplates = contentChildren(DataTableColumnDirective);

  /**
   * Row Detail templates gathered from the ContentChild
   */
  readonly rowDetail = contentChild(DatatableRowDetailDirective);

  /**
   * Group Header templates gathered from the ContentChild
   */
  readonly groupHeader = contentChild(DatatableGroupHeaderDirective);

  /**
   * Footer template gathered from the ContentChild
   */
  readonly footer = contentChild(DatatableFooterDirective);

  /**
   * Reference to the body component for manually
   * invoking functions on the body.
   */
  readonly bodyComponent = viewChild(DataTableBodyComponent);

  /**
   * Returns if all rows are selected.
   */
  get allRowsSelected(): boolean {
    const selected = this.selected();
    const rows = this.rowsInput();
    let allRowsSelected = rows && selected && selected.length === rows.length;

    const bodyComponent = this.bodyComponent();
    if (bodyComponent && this.selectAllRowsOnPage()) {
      const indexes = bodyComponent.indexes;
      const rowsOnPage = indexes.last - indexes.first;
      allRowsSelected = this.selected().length === rowsOnPage;
    }

    return this.selected() && rows && rows.length !== 0 && allRowsSelected;
  }

  /**
   * Returns the clamped current offset (page - 1).
   */
  get currentOffset(): number {
    return Math.max(Math.min(this._offsetSignal(), Math.ceil(this.rowCount / this.pageSize) - 1), 0);
  }

  element: HTMLElement;
  _innerWidth: number;
  pageSize: number;
  bodyHeight: number;
  rowCount: number = 0;
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  rowDiffer: KeyValueDiffer<{}, {}>;

  _offsetX = new BehaviorSubject(0);
  _internalRows: Array<Record<string, unknown>>;
  _internalColumns: Array<TableColumn>;
  _subscriptions: Array<Subscription> = [];

  constructor() {
    const element = inject(ElementRef);
    const differs = inject(KeyValueDiffers);

    // get ref to elm for measuring
    this.element = element.nativeElement;
    this.rowDiffer = differs.find({}).create();

    // apply global settings from Module.forRoot
    if (this.configuration?.messages) {
      this.messages.set({ ...this.configuration.messages });
    }

    // effect: calls translateColumnsFromArray() when projected column template content
    // changes — translateColumnsFromArray() is an imperative host-component API that
    // processes column definitions outside Angular's reactive model.
    effect(() => {
      const templates = this.columnTemplates();
      if (templates && templates.length) {
        this.translateColumnsFromArray([...templates]);
      }
    });

    // effect: schedules deferred input processing when rows change — scheduleApplyInputs()
    // is imperative; deferral prevents ExpressionChangedAfterItHasBeenCheckedError.
    effect(() => {
      const rows = this.rowsInput();
      // track the signal read so effect re-runs on change
      void rows;
      this.scheduleApplyInputs();
    });

    // effect: schedules deferred input processing when columns change — same rationale as rows.
    effect(() => {
      const cols = this.columnsInput();
      void cols;
      this.scheduleApplyInputs();
    });

    // React to limit changes — recalculate sizes
    effect(() => {
      void this.limitInput();
      this.recalculate();
    });

    // React to count changes — recalculate sizes
    effect(() => {
      void this.countInput();
      this.recalculate();
    });
  }

  /**
   * Lifecycle hook that is called after data-bound
   * properties of a directive are initialized.
   */
  ngOnInit(): void {
    // need to call this immediatly to size
    // if the table is hidden the visibility
    // listener will invoke this itself upon show
    this.recalculate();
  }

  /**
   * Lifecycle hook that is called after a component's
   * view has been fully initialized.
   */
  ngAfterViewInit(): void {
    if (!this.externalSorting()) {
      this.sortInternalRows();
    }

    // this has to be done to prevent the change detection
    // tree from freaking out because we are readjusting
    if (typeof requestAnimationFrame === 'undefined') {
      return;
    }

    requestAnimationFrame(() => {
      this.recalculate();

      // emit page for virtual server-side kickoff
      if (this.externalPaging() && this.scrollbarV()) {
        this.page.emit({
          count: this.countInput(),
          pageSize: this.pageSize,
          limit: this.limitInput(),
          offset: 0
        });
      }
    });
  }

  /**
   * Lifecycle hook that is called after a component's
   * content has been fully initialized.
   */
  ngAfterContentInit(): void {
    this.listenForColumnInputChanges();
  }

  /**
   * This will be used when displaying or selecting rows.
   * when tracking/comparing them, we'll use the value of this fn,
   *
   * (`fn(x) === fn(y)` instead of `x === y`)
   */
  readonly rowIdentity = input<(x: unknown) => unknown>((x: unknown) => {
    if (this.groupRowsBy()) {
      // each group in groupedRows are stored as {key, value: [rows]},
      // where key is the groupRowsBy index
      return (x as Record<string, unknown>)['key'];
    } else {
      return x;
    }
  });

  /**
   * Translates the templates to the column objects
   */
  translateColumns(val: Array<DataTableColumnDirective> | { toArray: () => Array<DataTableColumnDirective> }): void {
    if (val) {
      const arr = Array.isArray(val) ? val : (typeof (val as { toArray?: () => Array<DataTableColumnDirective> }).toArray === 'function' ? (val).toArray() : []);
      this.translateColumnsFromArray(arr);
    }
  }

  /**
   * Translates an already-resolved array of column directives into the
   * `_internalColumns` data structure used by the header and body.
   */
  translateColumnsFromArray(arr: Array<DataTableColumnDirective>): void {
    if (arr && arr.length) {
      this._internalColumns = translateTemplates(arr);
      // Set column defaults immediately so $$valueGetter is available before
      // change detection propagates the new columns to body cells.
      // Full recalculation is still deferred to avoid changing values during current CD.
      setColumnDefaults(this._internalColumns);
      this.scheduleApplyInputs();
    }
  }

  /**
   * Creates a map with the data grouped by the user choice of grouping index
   *
   * @param originalArray the original array passed via parameter
   * @param groupBy  the index of the column to group the data by
   */
  groupArrayBy(originalArray: Array<unknown>, groupBy: string): Array<{ key: unknown; value: Array<unknown> }> {
    // create a map to hold groups with their corresponding results
    const map = new Map<unknown, Array<unknown>>();

    originalArray.forEach((item: unknown) => {
      const key = (item as Record<string, unknown>)[groupBy];
      if (!map.has(key)) {
        map.set(key, [item]);
      } else {
        map.get(key).push(item);
      }
    });

    const addGroup = (key: unknown, value: Array<unknown>): { key: unknown; value: Array<unknown> } => ({ key, value });

    // convert map back to a simple array of objects
    return Array.from(map, (x: [unknown, Array<unknown>]) => addGroup(x[0], x[1]));
  }

  /*
   * Lifecycle hook that is called when Angular dirty checks a directive.
   */
  ngDoCheck(): void {
    if (this.rowDiffer.diff(this.rowsInput() as unknown as Record<string, unknown>)) {
      if (!this.externalSorting()) {
        this.sortInternalRows();
      } else {
        this._internalRows = [...this.rowsInput()] as Array<Record<string, unknown>>;
      }

      // auto group by parent on new update
      this._internalRows = groupRowsByParents(
        this._internalRows,
        optionalGetterForProp(this.treeFromRelation()),
        optionalGetterForProp(this.treeToRelation())
      ) as Array<Record<string, unknown>>;
      this.cd.markForCheck();
    }
  }

  /**
   * Recalc's the sizes of the grid.
   *
   * Updated automatically on changes to:
   *
   *  - Columns
   *  - Rows
   *  - Paging related
   *
   * Also can be manually invoked or upon window resize.
   */
  recalculate(): void {
    this.recalculateDims();
    this.recalculateColumns();
    this.cd.markForCheck();
  }

  /**
   * Window resize handler to update sizes.
   */

  @throttleable(5)
  onWindowResize(): void {
    this.recalculate();
  }

  /**
   * Recalulcates the column widths based on column width
   * distribution mode and scrollbar offsets.
   */
  recalculateColumns(
    columns: Array<unknown> = this._internalColumns,
    forceIdx: number = -1,
    allowBleed: boolean = this.scrollbarH()
  ): Array<unknown> | undefined {
    if (!columns) {return undefined;}

    let width = this._innerWidth;
    if (this.scrollbarV()) {
      width = width - this.scrollbarHelper.width;
    }

    const columnMode = this.columnMode();
    if (columnMode === ColumnMode.force) {
      forceFillColumnWidths(columns, width, forceIdx, allowBleed);
    } else if (columnMode === ColumnMode.flex) {
      adjustColumnWidths(columns, width);
    }

    // Width helpers mutate column objects in place; refresh array reference so
    // header/body inputs run again and recompute inline group widths.
    if (columns === this._internalColumns) {
      this._internalColumns = [...this._internalColumns];
    }
    return columns;
  }

  /**
   * Recalculates the dimensions of the table size.
   * Internally calls the page size and row count calcs too.
   *
   */
  recalculateDims(): void {
    const dims = this.dimensionsHelper.getDimensions(this.element);
    this._innerWidth = Math.floor(dims.width);

    if (this.scrollbarV()) {
      let height = dims.height;
      const headerHeight = this.headerHeight();
      if (headerHeight && headerHeight !== 'auto') {height = height - headerHeight;}
      const footerHeight = this.footerHeight();
      if (footerHeight && footerHeight !== 'auto') {height = height - footerHeight;}
      this.bodyHeight = height;
    }

    this.recalculatePages();
  }

  /**
   * Recalculates the pages after a update.
   */
  recalculatePages(): void {
    this.pageSize = this.calcPageSize();
    this.rowCount = this.calcRowCount();
  }

  /**
   * Body triggered a page event.
   */
  onBodyPage({ offset }: { offset: number }): void {
    // Avoid pagination caming from body events like scroll when the table
    // has no virtualization and the external paging is enable.
    // This means, let's the developer handle pagination by my him(her) self
    if (this.externalPaging() && !this.virtualization()) {
      return;
    }

    this._offsetSignal.set(offset);

    this.page.emit({
      count: this.countInput(),
      pageSize: this.pageSize,
      limit: this.limitInput(),
      offset: this.currentOffset
    });
  }

  /**
   * The body triggered a scroll event.
   */
  onBodyScroll(event: { offsetY: number; offsetX: number }): void {
    this._offsetX.next(event.offsetX);
    this.scroll.emit(event);
    this.cd.detectChanges();
  }

  /**
   * The footer triggered a page event.
   */
  onFooterPage(event: { page: number }): void {
    this._offsetSignal.set(event.page - 1);
    this.bodyComponent().updateOffsetY(this.currentOffset);

    this.page.emit({
      count: this.countInput(),
      pageSize: this.pageSize,
      limit: this.limitInput(),
      offset: this.currentOffset
    });

    if (this.selectAllRowsOnPage()) {
      this.selected.set([]);
      this.select.emit({
        selected: this.selected()
      });
    }
  }

  /**
   * Recalculates the sizes of the page
   */
  calcPageSize(val: Array<unknown> = this.rowsInput()): number {
    // Keep the page size constant even if the row has been expanded.
    // This is because an expanded row is still considered to be a child of
    // the original row.  Hence calculation would use rowHeight only.
    if (this.scrollbarV() && this.virtualization()) {
      const size = Math.ceil(this.bodyHeight / (this.rowHeight() as number));
      return Math.max(size, 0);
    }

    // if limit is passed, we are paging
    if (this.limitInput() !== undefined) {
      return this.limitInput();
    }

    // otherwise use row length
    if (val) {
      return val.length;
    }

    // other empty :(
    return 0;
  }

  /**
   * Calculates the row count.
   */
  calcRowCount(val: Array<unknown> = this.rowsInput()): number {
    if (!this.externalPaging()) {
      if (!val) {return 0;}

      const groupedRows = this.groupedRows();
      if (groupedRows) {
        return groupedRows.length;
      } else if (this.treeFromRelation != null && this.treeToRelation != null) {
        return this._internalRows?.length ?? 0;
      } else {
        return val.length;
      }
    }

    return this.countInput();
  }

  /**
   * The header triggered a contextmenu event.
   */
  onColumnContextmenu({ event, column }: { event: MouseEvent; column: TableColumn }): void {
    this.tableContextmenu.emit({ event, type: ContextmenuType.header, content: column });
  }

  /**
   * The body triggered a contextmenu event.
   */
  onRowContextmenu({ event, row }: { event: MouseEvent; row: unknown }): void {
    this.tableContextmenu.emit({ event, type: ContextmenuType.body, content: row });
  }

  /**
   * The header triggered a column resize event.
   */
  onColumnResize({ column, newValue }: { column: TableColumn; newValue: number }): void {
    /* Safari/iOS 10.2 workaround */
    if (column === undefined) {
      return;
    }

    let idx: number;
    const cols = this._internalColumns.map((c: TableColumn, i: number) => {
      c = { ...c };

      if (c.$$id === column.$$id) {
        idx = i;
        c.width = newValue;

        // set this so we can force the column
        // width distribution to be to this value
        c.$$oldWidth = newValue;
      }

      return c;
    });

    this.recalculateColumns(cols, idx);
    this._internalColumns = cols;

    this.resize.emit({
      column,
      newValue
    });
  }

  /**
   * The header triggered a column re-order event.
   */
  onColumnReorder({ column, newValue, prevValue }: { column: TableColumn; newValue: number; prevValue: number }): void {
    const cols = this._internalColumns.map((c: TableColumn) => ({ ...c }));

    if (this.swapColumns()) {
      const prevCol = cols[newValue];
      cols[newValue] = column;
      cols[prevValue] = prevCol;
    } else if (newValue > prevValue) {
      const movedCol = cols[prevValue];
      for (let i = prevValue; i < newValue; i++) {
        cols[i] = cols[i + 1];
      }
      cols[newValue] = movedCol;
    } else {
      const movedCol = cols[prevValue];
      for (let i = prevValue; i > newValue; i--) {
        cols[i] = cols[i - 1];
      }
      cols[newValue] = movedCol;
    }

    this._internalColumns = cols;

    this.reorder.emit({
      column,
      newValue,
      prevValue
    });
  }

  /**
   * The header triggered a column sort event.
   */
  onColumnSort(event: { sorts: Array<SortPropDir> }): void {
    // clean selected rows
    if (this.selectAllRowsOnPage()) {
      this.selected.set([]);
      this.select.emit({
        selected: this.selected()
      });
    }

    this.sorts.set(event.sorts);

    // this could be optimized better since it will resort
    // the rows again on the 'push' detection...
    if (this.externalSorting() === false) {
      // don't use normal setter so we don't resort
      this.sortInternalRows();
    }

    // auto group by parent on new update
    this._internalRows = groupRowsByParents(
      this._internalRows,
      optionalGetterForProp(this.treeFromRelation()),
      optionalGetterForProp(this.treeToRelation())
    ) as Array<Record<string, unknown>>;

    // Always go to first page when sorting to see the newly sorted data
    this._offsetSignal.set(0);
    this.bodyComponent().updateOffsetY(this.currentOffset);
    this.sort.emit(event);
  }

  /**
   * Toggle all row selection
   */
  onHeaderSelect(): void {
    const bodyComponent = this.bodyComponent();
    if (bodyComponent && this.selectAllRowsOnPage()) {
      // before we splice, chk if we currently have all selected
      const first = bodyComponent.indexes.first;
      const last = bodyComponent.indexes.last;
      const allSelected = this.selected().length === last - first;

      // remove all existing either way
      this.selected.set([]);

      // do the opposite here
      if (!allSelected) {
        this.selected().push(...this._internalRows.slice(first, last));
      }
    } else {
      // before we splice, chk if we currently have all selected
      const allSelected = this.selected().length === this.rowsInput().length;
      // remove all existing either way
      this.selected.set([]);
      // do the opposite here
      if (!allSelected) {
        this.selected().push(...this.rowsInput());
      }
    }

    this.select.emit({
      selected: this.selected()
    });
  }

  /**
   * A row was selected from body
   */
  onBodySelect(event: unknown): void {
    this.select.emit(event as { selected: Array<unknown> });
  }

  /**
   * A row was expanded or collapsed for tree
   */
  onTreeAction(event: { row: unknown }): void {
    const row = event.row;
    // TODO: For duplicated items this will not work
    const rowIndex = (this.rowsInput() as Array<Record<string, unknown>>).findIndex((r: Record<string, unknown>) => r[this.treeToRelation()] === (event.row as Record<string, unknown>)[this.treeToRelation()]);
    this.treeAction.emit({ row, rowIndex });
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  /**
   * listen for changes to input bindings of all DataTableColumnDirective and
   * re-run column translation so cell/header bindings reflect the new values.
   */
  private listenForColumnInputChanges(): void {
    this._subscriptions.push(
      this.columnChangesService.columnInputChanges$.subscribe(() => {
        const templates = this.columnTemplates();
        if (templates && templates.length) {
          this.translateColumnsFromArray([...templates]);
        }
      })
    );
  }

  private sortInternalRows(): void {
    this._internalRows = sortRows(this._internalRows, this._internalColumns, this.sorts()) as Array<Record<string, unknown>>;
  }

  // ===== Deferred apply to avoid ExpressionChangedAfterItHasBeenCheckedError =====
  private _applyScheduled = false;
  private scheduleApplyInputs(): void {
    if (this._applyScheduled) {return;}
    this._applyScheduled = true;
    Promise.resolve().then(() => {
      this._applyScheduled = false;
      this.applyInputs();
    });
  }

  private applyInputs(): void {
    const cols = this.columnsInput();
    // Process columns
    if (cols) {
      this._internalColumns = [...cols];
      setColumnDefaults(this._internalColumns);
      this.recalculateColumns();
    } else if (this._internalColumns) {
      // If columns were provided via content templates
      setColumnDefaults(this._internalColumns);
      this.recalculateColumns();
    }

    // Process rows
    const rows = this.rowsInput();
    if (rows) {
      this._internalRows = [...rows] as Array<Record<string, unknown>>;
      if (!this.externalSorting()) {
        this.sortInternalRows();
      }
      this._internalRows = groupRowsByParents(
        this._internalRows,
        optionalGetterForProp(this.treeFromRelation()),
        optionalGetterForProp(this.treeToRelation())
      ) as Array<Record<string, unknown>>;
    }

    // Recalculate sizes/etc
    this.recalculate();

    this.cd.markForCheck();
  }
}
